import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';

export default function OperatorManager() {
  const [operators, setOperators]   = useState([]);
  const [allUsers, setAllUsers]     = useState([]);
  const [nocTypes, setNocTypes]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [operatorLabel, setOperatorLabel] = useState('');
  const [nocAccess, setNocAccess]   = useState(new Set());
  const [saving, setSaving]         = useState(false);
  const [editModal, setEditModal]   = useState(null); // operator being edited

  const load = async () => {
    setLoading(true);
    try {
      const [opRes, nocRes] = await Promise.all([
        api.get('/operators'),
        api.get('/masters/noc-types'),
      ]);
      setOperators(opRes.operators || []);
      setNocTypes(nocRes.noc_types || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const searchUsers = async (q) => {
    if (q.length < 3) return;
    try {
      const r = await api.get(`/masters/users?search=${q}`);
      setAllUsers(r.users || []);
    } catch {}
  };

  const openAdd = () => {
    setSelectedUser(null);
    setOperatorLabel('');
    setNocAccess(new Set());
    setSearch('');
    setAllUsers([]);
    setShowAddModal(true);
  };

  const openEdit = async (op) => {
    setEditModal(op);
    setOperatorLabel(op.operator_label || '');
    try {
      const r = await api.get(`/user-noc-access/${op.id}`);
      setNocAccess(new Set(r.noc_type_ids || []));
    } catch { setNocAccess(new Set()); }
  };

  const saveOperator = async () => {
    const userId = selectedUser?.id || editModal?.id;
    if (!userId) return toast.error('Select a user first');
    setSaving(true);
    try {
      // Set as operator
      await api.post('/operators/set', {
        user_id: userId,
        is_operator: 1,
        operator_label: operatorLabel || 'Operator',
      });
      // Set NOC access
      await api.post('/user-noc-access', {
        user_id: userId,
        noc_type_ids: Array.from(nocAccess),
      });
      toast.success('Operator saved!');
      setShowAddModal(false);
      setEditModal(null);
      load();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const removeOperator = async (op) => {
    if (!window.confirm(`Remove operator access from ${op.name}?`)) return;
    try {
      await api.post('/operators/set', { user_id: op.id, is_operator: 0, operator_label: null });
      await api.post('/user-noc-access', { user_id: op.id, noc_type_ids: [] });
      toast.success('Operator removed');
      load();
    } catch { toast.error('Failed'); }
  };

  const toggleNoc = (id) => {
    setNocAccess(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  // ── Modal for add/edit ────────────────────────────────────────
  const OperatorModal = ({ isEdit }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg text-gray-800">
              {isEdit ? `✏️ Edit Operator: ${editModal?.name}` : '➕ Add New Operator'}
            </h3>
            <button onClick={() => { setShowAddModal(false); setEditModal(null); }}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none">✕</button>
          </div>

          {/* User search (only for add) */}
          {!isEdit && (
            <div className="mb-4">
              <label className="form-label">Search Citizen/User</label>
              <input className="form-input" placeholder="Type name or mobile (min 3 chars)"
                value={search}
                onChange={e => { setSearch(e.target.value); searchUsers(e.target.value); }} />
              {allUsers.length > 0 && !selectedUser && (
                <div className="border border-gray-200 rounded-xl mt-1 max-h-40 overflow-y-auto">
                  {allUsers.map(u => (
                    <div key={u.id}
                      onClick={() => { setSelectedUser(u); setSearch(u.name + ' — ' + u.mobile); setAllUsers([]); }}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 cursor-pointer border-b last:border-0">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-sm flex-shrink-0">
                        {u.name?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{u.name}</p>
                        <p className="text-xs text-gray-500">📱 {u.mobile}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {selectedUser && (
                <div className="mt-2 flex items-center gap-2 p-2.5 bg-green-50 border border-green-200 rounded-xl">
                  <span className="text-green-600">✅</span>
                  <div className="flex-1 text-sm">
                    <span className="font-medium text-gray-800">{selectedUser.name}</span>
                    <span className="text-gray-500 ml-2">📱 {selectedUser.mobile}</span>
                  </div>
                  <button onClick={() => { setSelectedUser(null); setSearch(''); }}
                    className="text-red-400 text-xs hover:text-red-600">Change</button>
                </div>
              )}
            </div>
          )}

          {/* Operator label */}
          <div className="mb-4">
            <label className="form-label">Operator Label / Name</label>
            <input className="form-input" placeholder="e.g. Counter 1, Zone Operator, VDA Operator"
              value={operatorLabel}
              onChange={e => setOperatorLabel(e.target.value)} />
            <p className="text-xs text-gray-400 mt-1">This label will be shown on their dashboard</p>
          </div>

          {/* NOC Access */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="form-label mb-0">NOC Types Access</label>
              <div className="flex gap-2">
                <button onClick={() => setNocAccess(new Set(nocTypes.map(n => n.id)))}
                  className="text-xs text-blue-600 hover:underline">Select All</button>
                <button onClick={() => setNocAccess(new Set())}
                  className="text-xs text-gray-400 hover:underline">Clear</button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-52 overflow-y-auto pr-1">
              {nocTypes.map(noc => (
                <label key={noc.id}
                  className={`flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer transition-all text-sm
                    ${nocAccess.has(noc.id) ? 'border-blue-400 bg-blue-50 text-blue-800' : 'border-gray-200 text-gray-700 hover:border-blue-200'}`}>
                  <input type="checkbox" checked={nocAccess.has(noc.id)}
                    onChange={() => toggleNoc(noc.id)}
                    className="w-4 h-4 rounded text-blue-600 flex-shrink-0" />
                  <span className="font-medium truncate">{noc.noc_name}</span>
                  <span className="text-xs font-mono text-gray-400 flex-shrink-0">{noc.noc_code}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-2">
              {nocAccess.size === 0
                ? '⚠️ No access — will see default NOCs only'
                : nocAccess.size === nocTypes.length
                  ? '✅ Full access to all NOC types'
                  : `✅ ${nocAccess.size} NOC type(s) selected`}
            </p>
          </div>

          <div className="flex gap-3">
            <button onClick={saveOperator} disabled={saving || (!isEdit && !selectedUser)}
              className="btn-primary flex-1 py-2.5" >
              {saving ? '⏳ Saving...' : isEdit ? '💾 Update Operator' : '✅ Add as Operator'}
            </button>
            <button onClick={() => { setShowAddModal(false); setEditModal(null); }}
              className="btn-secondary px-6">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">👷 Operator Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            Any citizen can be made an operator with custom NOC access
          </p>
        </div>
        <button onClick={openAdd} className="btn-primary">
          ➕ Add Operator
        </button>
      </div>

      <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800 mb-6">
        <p className="font-semibold mb-1">ℹ️ How Operators Work:</p>
        <p className="text-xs text-blue-700">
          Operators are citizens with special access. They login with OTP like normal citizens but see the NOC types you assign to them.
          Unlike regular citizens (who see only Dhara80 + Land Use by default), operators can see ALL or specific NOC types you choose.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
        </div>
      ) : operators.length === 0 ? (
        <div className="card text-center py-16">
          <p className="text-5xl mb-3">👷</p>
          <p className="text-lg font-semibold text-gray-700">No Operators Yet</p>
          <p className="text-sm text-gray-400 mt-1">Add a citizen as operator to give them extended NOC access</p>
          <button onClick={openAdd} className="btn-primary mt-4">➕ Add First Operator</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {operators.map(op => (
            <div key={op.id} className="card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-lg flex-shrink-0">
                    {op.name?.[0]?.toUpperCase() || 'O'}
                  </div>
                  <div>
                    <p className="font-bold text-gray-800">{op.name}</p>
                    <p className="text-xs text-gray-500">📱 {op.mobile}</p>
                    {op.operator_label && (
                      <span className="text-xs bg-blue-100 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full font-medium mt-1 inline-block">
                        🏷️ {op.operator_label}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => openEdit(op)}
                    className="text-xs btn-secondary px-3 py-1.5">✏️ Edit</button>
                  <button onClick={() => removeOperator(op)}
                    className="text-xs bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 px-3 py-1.5 rounded-lg">
                    🗑️ Remove
                  </button>
                </div>
              </div>
              <OperatorNocBadges userId={op.id} nocTypes={nocTypes} />
            </div>
          ))}
        </div>
      )}

      {showAddModal && <OperatorModal isEdit={false} />}
      {editModal && <OperatorModal isEdit={true} />}
    </div>
  );
}

// Small component to show NOC access badges
function OperatorNocBadges({ userId, nocTypes }) {
  const [ids, setIds] = useState([]);
  useEffect(() => {
    api.get(`/user-noc-access/${userId}`)
      .then(r => setIds(r.noc_type_ids || []))
      .catch(() => {});
  }, [userId]);

  if (ids.length === 0) return (
    <p className="text-xs text-orange-500 mt-3">⚠️ No specific access — using default</p>
  );

  const accessNocs = nocTypes.filter(n => ids.includes(n.id));
  return (
    <div className="mt-3 flex flex-wrap gap-1.5">
      {ids.length === nocTypes.length
        ? <span className="text-xs bg-green-100 text-green-700 border border-green-200 px-2 py-0.5 rounded-full">✅ All NOC Types</span>
        : accessNocs.map(n => (
          <span key={n.id} className="text-xs bg-gray-100 text-gray-600 border border-gray-200 px-2 py-0.5 rounded-full">
            {n.noc_name}
          </span>
        ))}
    </div>
  );
}